import React, { useState, useEffect, useCallback, useMemo } from 'react';

function ExportDialog({ isOpen, onClose, csvHeaders, csvRows, onExport, defaultProjectId, defaultSectionId, defaultSuiteId, rowReviews, rowExports, fileStatus }) {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [suiteId, setSuiteId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [forceImport, setForceImport] = useState(false);
  const [importModes, setImportModes] = useState({});

  // Determine which rows are displayable based on file status and rowReviews
  const displayableRows = useMemo(() => {
    if (fileStatus === 'Partially Approved') {
      // Only show rows that have at least one approved decision
      return csvRows.map((row, i) => ({ row, originalIndex: i }))
        .filter(({ originalIndex }) => {
          const reviews = rowReviews?.[String(originalIndex)];
          return reviews && reviews.some(r => r.decision === 'approved');
        });
    }
    // For "Approved" or other statuses, show all rows
    return csvRows.map((row, i) => ({ row, originalIndex: i }));
  }, [csvRows, fileStatus, rowReviews]);

  const isRowPreviouslyExported = useCallback((originalIndex) => {
    return rowExports && rowExports[String(originalIndex)] != null;
  }, [rowExports]);

  const isRowSelectable = useCallback((originalIndex) => {
    if (!isRowPreviouslyExported(originalIndex)) return true;
    return forceImport;
  }, [isRowPreviouslyExported, forceImport]);

  useEffect(() => {
    if (isOpen) {
      setSelectedRows(new Set());
      setSelectAll(false);
      setProjectId(defaultProjectId || '');
      setSectionId(defaultSectionId || '');
      setSuiteId(defaultSuiteId || '');
      setExporting(false);
      setError('');
      setForceImport(false);
      setImportModes({});
    }
  }, [isOpen, defaultProjectId, defaultSectionId, defaultSuiteId]);


  const handleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedRows(new Set());
      setSelectAll(false);
    } else {
      const selectable = displayableRows
        .filter(({ originalIndex }) => isRowSelectable(originalIndex))
        .map(({ originalIndex }) => originalIndex);
      setSelectedRows(new Set(selectable));
      setSelectAll(true);
    }
  }, [selectAll, displayableRows, isRowSelectable]);

  const handleRowToggle = useCallback((originalIndex) => {
    if (!isRowSelectable(originalIndex)) return;
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(originalIndex)) {
        next.delete(originalIndex);
      } else {
        next.add(originalIndex);
      }
      const selectableCount = displayableRows.filter(({ originalIndex: oi }) => isRowSelectable(oi)).length;
      setSelectAll(next.size === selectableCount && selectableCount > 0);
      return next;
    });
  }, [displayableRows, isRowSelectable]);

  const handleForceImportToggle = useCallback(() => {
    setForceImport(prev => {
      const next = !prev;
      if (!next) {
        // When disabling force import, deselect previously exported rows and clear import modes
        setSelectedRows(prevSelected => {
          const next = new Set(prevSelected);
          for (const idx of prevSelected) {
            if (isRowPreviouslyExported(idx)) {
              next.delete(idx);
            }
          }
          return next;
        });
        setImportModes({});
        setSelectAll(false);
      } else {
        // Default import mode to "override" for all previously exported rows
        const modes = {};
        displayableRows.forEach(({ originalIndex }) => {
          if (isRowPreviouslyExported(originalIndex)) {
            modes[String(originalIndex)] = 'override';
          }
        });
        setImportModes(modes);
      }
      return next;
    });
  }, [displayableRows, isRowPreviouslyExported]);

  const handleImportModeChange = useCallback((originalIndex, mode) => {
    setImportModes(prev => ({ ...prev, [String(originalIndex)]: mode }));
  }, []);

  const canExport = selectedRows.size > 0 && projectId.trim() !== '' && sectionId.trim() !== '' && suiteId.trim() !== '';

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const payload = {
        selectedRowIndices: Array.from(selectedRows).sort((a, b) => a - b),
        projectId: parseInt(projectId, 10),
        sectionId: parseInt(sectionId, 10),
        suiteId: parseInt(suiteId, 10),
        forceImport,
        importMode: forceImport ? importModes : {}
      };
      await onExport(payload);
      onClose();
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  const hasPreviouslyExportedRows = displayableRows.some(({ originalIndex }) => isRowPreviouslyExported(originalIndex));

  return (
    <div className="export-dialog-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Import to TestRail">
      <div className="export-dialog" onClick={e => e.stopPropagation()}>
        <div className="export-dialog-header">
          <h3>Import to TestRail</h3>
          <button className="export-dialog-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="export-dialog-config">
          <div className="export-dialog-field">
            <label htmlFor="export-project-id">Project ID</label>
            <input
              id="export-project-id"
              className="input"
              type="text"
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              placeholder="Enter Project ID"
            />
          </div>
          <div className="export-dialog-field">
            <label htmlFor="export-section-id">Section ID</label>
            <input
              id="export-section-id"
              className="input"
              type="text"
              value={sectionId}
              onChange={e => setSectionId(e.target.value)}
              placeholder="Enter Section ID"
            />
          </div>
          <div className="export-dialog-field">
            <label htmlFor="export-suite-id">Suite ID</label>
            <input
              id="export-suite-id"
              className="input"
              type="text"
              value={suiteId}
              onChange={e => setSuiteId(e.target.value)}
              placeholder="Enter Suite ID"
            />
          </div>
        </div>

        {hasPreviouslyExportedRows && (
          <div className="export-dialog-force-import">
            <label className="force-import-toggle">
              <input
                type="checkbox"
                checked={forceImport}
                onChange={handleForceImportToggle}
              />
              <span>Force Import — re-import previously exported rows</span>
            </label>
          </div>
        )}

        <div className="export-dialog-count">
          {selectedRows.size} of {displayableRows.length} rows selected
          {fileStatus === 'Partially Approved' && displayableRows.length < csvRows.length && (
            <span className="export-filter-note"> (showing approved rows only)</span>
          )}
        </div>

        <div className="export-dialog-table-wrapper">
          <table className="export-dialog-table">
            <thead>
              <tr>
                <th className="export-checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
                <th className="export-row-num">#</th>
                {forceImport && <th className="export-import-mode-col">Mode</th>}
                {csvHeaders.map((header, i) => (
                  <th key={i}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayableRows.map(({ row, originalIndex }) => {
                const exported = isRowPreviouslyExported(originalIndex);
                const selectable = isRowSelectable(originalIndex);
                const isSelected = selectedRows.has(originalIndex);
                const rowClassName = [
                  isSelected ? 'export-row-selected' : '',
                  exported && !forceImport ? 'export-row-previously-exported' : ''
                ].filter(Boolean).join(' ');

                return (
                  <tr key={originalIndex} className={rowClassName}>
                    <td className="export-checkbox-col">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleRowToggle(originalIndex)}
                        disabled={!selectable}
                        aria-label={`Select row ${originalIndex + 1}`}
                      />
                    </td>
                    <td className="export-row-num">
                      {originalIndex + 1}
                      {exported && <span className="export-badge" title="Previously exported to TestRail">↗</span>}
                    </td>
                    {forceImport && (
                      <td className="export-import-mode-col">
                        {exported && isSelected ? (
                          <div className="import-mode-radios">
                            <label>
                              <input
                                type="radio"
                                name={`import-mode-${originalIndex}`}
                                value="override"
                                checked={(importModes[String(originalIndex)] || 'override') === 'override'}
                                onChange={() => handleImportModeChange(originalIndex, 'override')}
                              />
                              Override
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`import-mode-${originalIndex}`}
                                value="duplicate"
                                checked={importModes[String(originalIndex)] === 'duplicate'}
                                onChange={() => handleImportModeChange(originalIndex, 'duplicate')}
                              />
                              Duplicate
                            </label>
                          </div>
                        ) : exported ? (
                          <span className="export-mode-placeholder">—</span>
                        ) : null}
                      </td>
                    )}
                    {csvHeaders.map((header, colIndex) => (
                      <td key={colIndex}>{row[header]}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {error && <div className="error-message" role="alert">{error}</div>}

        <div className="export-dialog-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={exporting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleExport} disabled={!canExport || exporting}>
            {exporting ? 'Importing...' : 'Import Selected'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportDialog;
